/**
 * Modello della superficie **Monitor lista SLA** (piano Client sez. 5 F3a, sez. 6
 * riga 88 e sez. 8 riga 125).
 *
 * Cosa la superficie rende, e da dove viene:
 * - **KPI** (`on_time`/`at_risk`/`breached`/`total`) da `fetchSlaKpi`: sono
 *   conteggi del Server, mai ricalcolati sommando la pagina corrente;
 * - **lista** da `fetchSlaInstances` (`SlaInstance`), una riga **per istanza**
 *   (target + assegnatario), cosi' un processo con piu' attivita' parallele non
 *   viene appiattito in una riga sola;
 * - **livello di escalation raggiunto** (`escalation_level` + `escalation_levels`)
 *   reso come informazione **aggiuntiva**: la salute resta una delle tre del
 *   contratto (`SLA_HEALTH_*`) e un breach escalato resta un breach (sez. 5 F3a,
 *   sez. 14.1). Qui non esiste alcuna funzione che ricavi uno stato dalla durata.
 *
 * Contratti che il piano prescrive e che il payload C1 **non** porta. Sono
 * dichiarati, non inventati (nessun campo aggiunto al DTO):
 * - **copertura senza SLA**: `SlaKpiRow` (`Service.SLA.pas:784-816`) porta solo
 *   i quattro conteggi di salute e `GET /instances` (`Service.SLA.pas:727-768`)
 *   restituisce per costruzione solo istanze **con** SLA. Il numero dei target
 *   senza SLA non e' quindi calcolabile dal Monitor: esiste solo per nodo nel
 *   diagramma (`has_sla = false`, F3b, sez. 11.2). F3a rende la copertura come
 *   **N/D dichiarato** (`SLA_MONITOR_COVERAGE_SOURCE`), mai come zero o verde;
 * - **ordinamento a scelta dell'utente**: la rotta non ha parametri di
 *   ordinamento (`Controller.SLA.pas:309-331`: `level`, `health`, `page`,
 *   `pagesize`) e il Server ordina da se' per scadenza vigente
 *   (`Service.SLA.pas:768`: `effective_deadline_at asc, id asc`). Il Monitor
 *   mostra l'ordine del Server e **non** riordina nel browser: non offre alcun
 *   comando di ordinamento, che sarebbe inerte o ingannevole (riordinerebbe la
 *   sola pagina caricata);
 * - **freschezza e aggiornamento runtime (F3d)**: il contratto non porta un
 *   limite di validita' dei dati, quindi la soglia e' una scelta Client
 *   dichiarata (`SLA_MONITOR_STALE_AFTER_SECONDS`), valutata su un orologio che
 *   avanza (`SLA_RUNTIME_REFRESH_TICK_MS`); la rilettura avviene a rientro sul
 *   tab, al superamento di `next_clock_change_at` (`slaMonitorNeedsRefetch`) e a
 *   ogni cambio di filtro/pagina. Nessuna sottrazione di tempo di parete: il
 *   residuo reso e' quello del payload.
 */

import dayjs from "dayjs";
import {
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
  SLA_HEALTH_ON_TIME,
  SLA_TARGET_ACTIVITY,
  SLA_TARGET_PROCESS,
  isSlaEscalationLevel,
} from "model/slaModel";
import type {
  SlaHealth,
  SlaInstance,
  SlaInstancesQuery,
  SlaPaginationMeta,
  SlaTargetType,
} from "model/slaModel";

// ─── Chiavi di traduzione ────────────────────────────────────────────────────

/**
 * Chiavi usate dalla superficie Monitor. Raccolte qui e non sparse nei componenti
 * perche' il piano sez. 4 le fa concordare con Python P1 in un manifest:
 * `getTranslation` restituisce la chiave se il testo manca, quindi questo elenco
 * **e'** il manifest lato Monitor. La famiglia `sla.monitor.*` non e' ancora nel
 * catalogo P1 (residuo dichiarato): finche' manca, le label escono grezze e le
 * asserzioni "nessuna chiave `sla.*` residua" le **nominano**.
 *
 * Le chiavi gia' esistenti (`generic.all`, `generic.button.clear`,
 * `generic.button.cancel`, `generic.hours.caption`, `generic.minutes.caption`,
 * `generic.seconds.caption`, `sla.editor.fixture_notice`) sono riusate: non se ne
 * duplica il testo.
 */
export const SLA_MONITOR_TEXT_KEYS = {
  title: "sla.monitor.title",
  /** Filtro di livello: processo e attivita' sono liste separate (sez. 5 F3a). */
  levelLabel: "sla.monitor.level.label",
  levelProcess: "sla.monitor.level.process",
  levelActivity: "sla.monitor.level.activity",
  /** KPI cliccabili: ogni conteggio filtra la lista sullo stato scelto (sez. 5 F3a). */
  kpiOnTime: "sla.monitor.kpi.on_time",
  kpiAtRisk: "sla.monitor.kpi.at_risk",
  kpiBreached: "sla.monitor.kpi.breached",
  kpiTotal: "sla.monitor.kpi.total",
  /** Copertura: N/D dichiarato, la sorgente non esiste nel contratto (sopra). */
  coverageTitle: "sla.monitor.coverage.title",
  coverageWithoutSla: "sla.monitor.coverage.without_sla",
  coverageNotAvailable: "sla.monitor.coverage.not_available",
  coverageSourceMissing: "sla.monitor.coverage.source_missing",
  /** Filtri: su tablet/mobile finiscono in un drawer (sez. 6 riga 88). */
  filtersOpen: "sla.monitor.filters.open",
  /** Etichette di colonna/riga. */
  columnProcess: "sla.monitor.column.process",
  columnActivity: "sla.monitor.column.activity",
  columnAssignee: "sla.monitor.column.assignee",
  columnHealth: "sla.monitor.column.health",
  columnDeadline: "sla.monitor.column.deadline",
  columnRemaining: "sla.monitor.column.remaining",
  columnOverdue: "sla.monitor.column.overdue",
  columnEscalation: "sla.monitor.column.escalation",
  columnRevision: "sla.monitor.column.revision",
  /** Assegnatario: `iduser = 0` e' un task non ancora preso in carico (sez. 5 F3a). */
  assigneeUnassigned: "sla.monitor.assignee.unassigned",
  assigneeUnknown: "sla.monitor.assignee.unknown",
  /** I tre stati del contratto: nessun sesto (sez. 11.1). */
  healthOnTime: "sla.monitor.health.on_time",
  healthAtRisk: "sla.monitor.health.at_risk",
  healthBreached: "sla.monitor.health.breached",
  /** "livello n/m" accanto allo stato, mai al posto dello stato (sez. 14.1). */
  escalationLabel: "sla.monitor.escalation.label",
  escalationNone: "sla.monitor.escalation.none",
  /** Scadenza vigente prorogata da un override: l'originale non e' stata riscritta. */
  overridden: "sla.monitor.overridden",
  /** Timezone dichiarata accanto agli istanti (mai quella del browser, sez. 4). */
  timezone: "sla.monitor.timezone",
  /** Paginazione 50: si accumulano pagine con un comando esplicito (sez. 6 riga 88). */
  loadMore: "sla.monitor.load_more",
  listEnd: "sla.monitor.list_end",
  loading: "sla.monitor.loading",
  empty: "sla.monitor.empty",
  loadError: "sla.monitor.load_error",
  /** 403: la lettura del Monitor non e' admin-only, ma il contratto la prevede. */
  forbidden: "sla.monitor.forbidden",
  /** Dati non aggiornati: lo stato e i KPI non restano verdi (sez. 5 F3d). */
  stale: "sla.monitor.stale",
  asOf: "sla.monitor.as_of",
} as const;

// ─── Salute: tre stati, nessun sesto ─────────────────────────────────────────

/** I soli tre stati del contratto (`SLA_HEALTH_*`), nell'ordine dei KPI. */
export const SLA_MONITOR_HEALTH_STATES: readonly SlaHealth[] = [
  SLA_HEALTH_ON_TIME,
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
];

/**
 * Salute di una riga. Un valore fuori dai tre del contratto **non** produce un
 * quarto stato: torna `null` e la riga dichiara lo stato non reso invece di
 * inventare un badge (sez. 11.1, sez. 14.1).
 */
export const slaMonitorHealth = (
  value: number | null | undefined,
): SlaHealth | null =>
  typeof value === "number" &&
  SLA_MONITOR_HEALTH_STATES.includes(value as SlaHealth)
    ? (value as SlaHealth)
    : null;

/** Chiave di traduzione dello stato: la label non e' mai un enum. */
export const slaMonitorHealthKey = (health: SlaHealth): string => {
  switch (health) {
    case SLA_HEALTH_ON_TIME:
      return SLA_MONITOR_TEXT_KEYS.healthOnTime;
    case SLA_HEALTH_AT_RISK:
      return SLA_MONITOR_TEXT_KEYS.healthAtRisk;
    default:
      return SLA_MONITOR_TEXT_KEYS.healthBreached;
  }
};

// ─── Filtri (server-side) ────────────────────────────────────────────────────

/** 0 = entrambi i livelli: e' il default della rotta (`Controller.SLA.pas:319-320`). */
export const SLA_MONITOR_LEVEL_ALL = 0 as const;

/** Livelli ammessi dal filtro, come li legge il Server (`level`: 0/1/2). */
export const SLA_MONITOR_LEVELS: readonly (0 | SlaTargetType)[] = [
  SLA_MONITOR_LEVEL_ALL,
  SLA_TARGET_PROCESS,
  SLA_TARGET_ACTIVITY,
];

export const slaMonitorLevel = (
  value: number | undefined,
): 0 | SlaTargetType =>
  value === SLA_TARGET_PROCESS || value === SLA_TARGET_ACTIVITY
    ? value
    : SLA_MONITOR_LEVEL_ALL;

export const slaMonitorLevelKey = (
  level: 0 | SlaTargetType,
): string | null => {
  switch (level) {
    case SLA_TARGET_PROCESS:
      return SLA_MONITOR_TEXT_KEYS.levelProcess;
    case SLA_TARGET_ACTIVITY:
      return SLA_MONITOR_TEXT_KEYS.levelActivity;
    default:
      // "tutti": chiave gia' esistente, nessun duplicato `sla.monitor.*`.
      return "generic.all";
  }
};

/** -1 = nessun filtro di salute: e' il default della rotta. */
export const SLA_MONITOR_HEALTH_ALL = -1 as const;

/**
 * Query dei filtri. Il parametro di salute **assente** vale "tutti" lato Server
 * (`Controller.SLA.pas:322-323`): quando non c'e' filtro non lo si manda, cosi'
 * la richiesta resta leggibile e il Server usa il proprio default. `level` e'
 * sempre inviato (0 = entrambi ha lo stesso effetto dell'assenza).
 */
export const slaMonitorQuery = (
  level: 0 | SlaTargetType,
  health: SlaHealth | typeof SLA_MONITOR_HEALTH_ALL,
  page: number,
): SlaInstancesQuery =>
  health === SLA_MONITOR_HEALTH_ALL
    ? { level, page }
    : { level, health, page };

// ─── Paginazione 50 ──────────────────────────────────────────────────────────

/**
 * C'e' una pagina successiva? La decide il meta del Server
 * (`pagination.next_page_uri`, `Controller.SLA.pas:332-336`): il Client non
 * deduce "ultima pagina" dal numero di righe ricevute, perche' l'ultima pagina
 * puo' contenere esattamente 50 righe.
 */
export const slaMonitorHasMore = (
  pagination: SlaPaginationMeta | null | undefined,
): boolean => Boolean(pagination?.next_page_uri);

/** Pagina corrente dal meta (`curr_page` e' una stringa nel contratto). */
export const slaMonitorPageFromMeta = (
  pagination: SlaPaginationMeta | null | undefined,
  fallback: number,
): number => {
  const parsed = Number(pagination?.curr_page);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

// ─── Escalation raggiunta: informazione aggiuntiva, non sesto stato ──────────

/**
 * Livello raggiunto di un'istanza. `0` = nessuno (colonna `smallint NOT NULL
 * DEFAULT 0`), il massimo e' `escalation_levels` della policy dell'esecuzione
 * (fallback 1 quando il target non ha escalation). Il valore e' **read-only**:
 * lo scrive solo il worker (sez. 14.1). Non si deduce da `health`, dalle durate
 * ne' dal solo `escalation_enabled`.
 */
export interface SlaMonitorEscalation {
  /** Livello raggiunto, 0 = nessuno. */
  level: number;
  /** Massimo della voce di policy: 1..5 (fallback 1). */
  levels: number;
  /** true quando un livello e' stato raggiunto: la label e' "n/m". */
  reached: boolean;
}

export const slaMonitorEscalation = (
  instance: Pick<SlaInstance, "escalation_level" | "escalation_levels">,
): SlaMonitorEscalation => {
  const levels = isSlaEscalationLevel(instance.escalation_levels)
    ? instance.escalation_levels
    : 1;
  const level =
    Number.isInteger(instance.escalation_level) && instance.escalation_level > 0
      ? instance.escalation_level
      : 0;
  return { level, levels, reached: level > 0 };
};

/**
 * "n/m" come coppia di numeri, non come frase: `getTranslation` non interpola,
 * quindi i due valori si rendono accanto alla label, mai concatenati a un
 * frammento grammaticale. `null` quando nessun livello e' stato raggiunto: in
 * quel caso si mostra la chiave "nessuno", non uno zero di allarme (sez. 14.1).
 */
export const slaMonitorEscalationValue = (
  escalation: SlaMonitorEscalation,
): string | null =>
  escalation.reached ? `${escalation.level}/${escalation.levels}` : null;

// ─── Assegnatario ────────────────────────────────────────────────────────────

/** `iduser = 0` = task disponibile e non ancora preso in carico (sez. 5 F3a). */
export const slaMonitorIsUnassigned = (iduser: number): boolean => iduser === 0;

/** Riga utente della rotta `organizations/{id}/users` (stessa di `SlaEditor.tsx`). */
export interface SlaMonitorUserRow {
  id: number;
  username?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
}

/**
 * Nome leggibile dell'assegnatario: stessa regola di `NewProcessModule.getUsername`
 * (username, poi nome+cognome, poi email) usata da `SlaEditor.tsx` per i selettori.
 * `null` quando l'utente non e' nell'elenco: la riga dichiara l'id del payload
 * invece di mostrare un nome inventato.
 */
export const slaMonitorAssigneeName = (
  users: SlaMonitorUserRow[],
  iduser: number,
): string | null => {
  const user = users.find((candidate) => candidate.id === iduser);
  if (!user) return null;
  if (user.username) return user.username;
  if (user.firstname || user.lastname) {
    return (
      (user.firstname || "") +
      (user.firstname && user.lastname ? " " : "") +
      (user.lastname || "")
    );
  }
  return user.email || null;
};

// ─── Tempi e durate (nessun calcolo nel browser) ─────────────────────────────

/**
 * Istante reso nella timezone **dichiarata** dalla riga (`timezone_id`): la
 * timezone SLA/org non dipende dalla lingua ne' dal browser (sez. 4). Senza
 * timezone dichiarata si rende l'istante UTC con suffisso esplicito: non si usa
 * quella del browser.
 */
export const slaMonitorDateTime = (
  value: string | null | undefined,
  timezoneId: string | null | undefined,
): string => {
  if (!value) return "";
  const instant = dayjs(value);
  if (!instant.isValid()) return "";
  return timezoneId
    ? instant.tz(timezoneId).format("YYYY-MM-DD HH:mm")
    : `${instant.utc().format("YYYY-MM-DD HH:mm")}Z`;
};

/**
 * Unita' brevi delle durate SLA rese a video ("2 d 3 h", "14 h 42 min"). Solo
 * chiavi: il testo viene dal catalogo (`flowenti_messages`), nessun fallback.
 */
export const SLA_DURATION_UNIT_KEYS = {
  days: "sla.duration.days.short",
  hours: "sla.duration.hours.short",
  minutes: "sla.duration.minutes.short",
} as const;

/** Componente di una durata SLA: quantita' intera + chiave dell'unita'. */
export interface SlaDurationPart {
  value: number;
  unitKey: string;
  /** true per "<1 min": durata positiva sotto il minuto. */
  lessThan?: boolean;
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

/**
 * **Formatter unico delle durate SLA** (residuo/ritardo lavorativo in secondi,
 * come li manda il Server). Conversione di unita', al massimo due componenti:
 * - >= 1 giorno -> giorni + ore ("2 d 3 h", ore a zero omesse: "2 d");
 * - >= 1 ora -> ore + minuti ("14 h 42 min", minuti a zero omessi: "14 h");
 * - < 1 ora -> minuti ("42 min"); sotto il minuto "<1 min", zero = "0 min".
 * `null` = valore assente o non valido: chi chiama rende il testo N/D.
 */
export const slaDurationParts = (
  seconds: number | null | undefined,
): SlaDurationPart[] | null => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  const total = Math.floor(seconds);
  const { days, hours, minutes } = SLA_DURATION_UNIT_KEYS;
  if (total >= SECONDS_PER_DAY) {
    const d = Math.floor(total / SECONDS_PER_DAY);
    const h = Math.floor((total % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
    return h > 0
      ? [{ value: d, unitKey: days }, { value: h, unitKey: hours }]
      : [{ value: d, unitKey: days }];
  }
  if (total >= SECONDS_PER_HOUR) {
    const h = Math.floor(total / SECONDS_PER_HOUR);
    const m = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    return m > 0
      ? [{ value: h, unitKey: hours }, { value: m, unitKey: minutes }]
      : [{ value: h, unitKey: hours }];
  }
  if (total >= SECONDS_PER_MINUTE) {
    return [{ value: Math.floor(total / SECONDS_PER_MINUTE), unitKey: minutes }];
  }
  return total > 0
    ? [{ value: 1, unitKey: minutes, lessThan: true }]
    : [{ value: 0, unitKey: minutes }];
};

/**
 * Testo tradotto di `slaDurationParts` ("14 h 42 min"). `translate` risolve una
 * chiave del catalogo (di solito `key => getTranslation(translations, key)`).
 * `null` quando la durata non e' disponibile.
 */
export const slaDurationText = (
  seconds: number | null | undefined,
  translate: (key: string) => string,
): string | null => {
  const parts = slaDurationParts(seconds);
  if (!parts) return null;
  return parts
    .map((part) => `${part.lessThan ? "<" : ""}${part.value} ${translate(part.unitKey)}`)
    .join(" ");
};

// ─── Freschezza dei dati ─────────────────────────────────────────────────────

/**
 * Oltre questa distanza fra l'istante del Server (`as_of`) e il clock del
 * browser, i dati sono dichiarati **non aggiornati** e la superficie non mostra
 * stato verde (sez. 5 F3d, sez. 8 riga 135). Il contratto non porta un limite di
 * validita' e `sla_settings.maxnotificationlatencyseconds` e' admin-only: la
 * soglia e' quindi una scelta Client **dichiarata** (5 minuti, la stessa
 * grandezza della latenza massima di notifica di default dello schema), la
 * stessa per tutte le superfici SLA. Il test la fissa con il clock del browser e
 * un `as_of` noto, mai con attese reali.
 *
 * Da F3d la soglia e' valutata su un **orologio che avanza** (vedi
 * `SLA_RUNTIME_REFRESH_TICK_MS` e `useSlaRuntimeRefresh`): senza il tick lo stato
 * resterebbe verde finche' un'interazione non forza un ricalcolo.
 */
export const SLA_MONITOR_STALE_AFTER_SECONDS = 300;

/** L'istante piu' vecchio fra i dati resi: e' quello che governa la freschezza. */
export const slaMonitorOldestAsOf = (
  values: Array<string | null | undefined>,
): string | null => {
  const instants = values
    .filter((value): value is string => Boolean(value))
    .map((value) => ({ value, ms: dayjs(value).valueOf() }))
    .filter((entry) => Number.isFinite(entry.ms));
  if (instants.length === 0) return null;
  return instants.reduce((oldest, entry) =>
    entry.ms < oldest.ms ? entry : oldest,
  ).value;
};

/** true quando `asOf` e' piu' vecchio della soglia rispetto a `nowMs`. */
export const isSlaMonitorStale = (
  asOf: string | null | undefined,
  nowMs: number,
  staleAfterSeconds: number = SLA_MONITOR_STALE_AFTER_SECONDS,
): boolean => {
  if (!asOf) return false;
  const ms = dayjs(asOf).valueOf();
  if (!Number.isFinite(ms)) return false;
  return nowMs - ms > staleAfterSeconds * 1000;
};

// ─── Aggiornamento runtime (sez. 5 F3d) ─────────────────────────────────────

/**
 * Cadenza dell'orologio locale delle superfici SLA: un secondo. Serve **solo** a
 * rivalutare la freschezza (`as_of`) e la soglia dichiarata dal payload, senza
 * attendere un'interazione dell'utente. Non e' una simulazione del worker, non
 * tocca i payload e non introduce alcun calcolo di calendario o di giorni solari
 * nel browser (sez. 1, sez. 4 F1).
 */
export const SLA_RUNTIME_REFRESH_TICK_MS = 1000;

/**
 * Riga che dichiara il prossimo cambio di clock del payload: le istanze
 * (`SlaInstance.clock_running`/`next_clock_change_at`) e i nodi del diagramma
 * (`SlaDiagramNode`) portano entrambi questi campi.
 */
export interface SlaRuntimeClockRow {
  next_clock_change_at: string | null;
}

/**
 * true quando il clock del browser ha superato un `next_clock_change_at`
 * dichiarato dal payload: da quell'istante il payload non descrive piu' il clock
 * (pausa o **ripresa**) e i dati vanno **richiesti al Server**. E' il refresh "a
 * soglia/apertura" di F3d: la riapertura del calendario non si deduce nel browser
 * e la superficie non sottrae tempo di parete — si rilegge.
 *
 * Il confronto e' fra due istanti dichiarati (il clock del browser e
 * l'istante del Server), mai fra un istante e una durata lavorativa: nessuna
 * sottrazione solare durante una chiusura (sez. 5 F3d).
 */
export const slaMonitorNeedsRefetch = (
  rows: readonly SlaRuntimeClockRow[],
  nowMs: number,
): boolean =>
  rows.some((row) => {
    if (!row.next_clock_change_at) return false;
    const at = dayjs(row.next_clock_change_at).valueOf();
    return Number.isFinite(at) && nowMs >= at;
  });

// ─── Copertura senza SLA ─────────────────────────────────────────────────────

/**
 * Sorgente della copertura "target senza SLA". `unavailable` non e' un errore
 * transitorio: e' il contratto che non la espone (vedi testa del file).
 */
export const SLA_MONITOR_COVERAGE_SOURCE = "unavailable" as const;

/**
 * Il KPI non accetta il filtro di salute (`Controller.SLA.pas:353-361`): cambiare
 * il KPI selezionato **non** cambia i conteggi, cambia le righe. E' il contratto,
 * non un'incoerenza da correggere nel browser sommando la pagina corrente.
 */
export const SLA_MONITOR_KPI_QUERY_KEYS = ["level"] as const;
